import { type WritableProxy } from '.';

interface FieldConfig<T, IF extends boolean> {
    validator: (value: T) => boolean;
    partialValidator: (
        value: T
    ) => boolean | { isValid: boolean; isPartial: boolean };
    reset?: (prevValue: T) => T;
    comparator: (value: T, otherValue: T) => boolean;
    default: T;
    disabled: MaybeRefOrGetter<boolean>;
    required: MaybeRefOrGetter<boolean>;
    errorMessage: MaybeRefOrGetter<string>;
    requiredError: MaybeRefOrGetter<string>;
    ignoreTouch: MaybeRefOrGetter<boolean>;
    minLength: MaybeRefOrGetter<number>;
    maxLength: MaybeRefOrGetter<number>;
    reviver: (value: any) => T;
    hidden?: MaybeRefOrGetter<IF>;
    debugValue?: string;
}

interface Attrs<T> {
    required?: boolean;
    invalid?: boolean;
    disabled?: boolean;
    onBlur?: () => void;
    minLength?: number;
    maxLength?: number;
    modelValue?: T;
    'onUpdate:modelValue'?: (value: T | any) => void;
}

export class Field<IF extends boolean, T = any> {
    constructor(
        public model: WritableProxy<T>,
        config?: Partial<FieldConfig<T, IF>>,
        private onChange?: (value: T) => void
    ) {
        const defaultValue = config?.default ?? model.value;
        this.config = {
            default: defaultValue,
            comparator: config?.comparator ?? ((a, b) => a === b),
            disabled: config?.disabled ?? false,
            required: config?.required ?? false,
            validator: config?.validator ?? (() => true),
            requiredError:
                config?.requiredError ?? 'این فیلد نمی‌تواند خالی باشد.',
            errorMessage: config?.errorMessage ?? 'مقدار واردشده معتبر نیست.',
            ignoreTouch: config?.ignoreTouch ?? false,
            reviver: config?.reviver ?? (value => value),
            reset: config?.reset ?? (_ => defaultValue),
            hidden: (config?.hidden ?? false) as any,
            debugValue: config?.debugValue ?? '',
            minLength: config?.minLength ?? 0,
            maxLength: config?.maxLength ?? Infinity,
            partialValidator: config?.partialValidator ?? (() => false),
        };
        watch(model, value => {
            this.touched.value = true;
            this.editing.value = true;
            this.onChange?.(value);
            this.hasError.value = false;
        });
        watch(
            () => toValue(this.config.hidden),
            value => {
                if (value) {
                    this.reset();
                }
            }
        );
    }

    private config: Required<FieldConfig<T, IF>>;
    private hasError = ref(false);
    private touched = ref(false);
    private editing = ref(false);

    public get attrs(): IF extends false ? Attrs<T> : Attrs<T> | undefined {
        if (toValue(this.config.hidden)) {
            return undefined as any;
        }
        return {
            modelValue: this.model.value,
            'onUpdate:modelValue': value => {
                this.model.value = value;
            },
            disabled: this.disabled,
            onBlur: () => {
                this.touch();
                this.editing.value = false;
            },
            invalid: this.invalid,
            required: this.required,
            minLength: this.minLength,
            maxLength: this.maxLength,
        };
    }

    private get minLength() {
        return toValue(this.config.minLength);
    }

    private get maxLength() {
        return toValue(this.config.maxLength);
    }

    get isFieldDisabled() {
        return toValue(this.config.hidden) || toValue(this.config.disabled);
    }

    public reset() {
        this.model.value = this.config.reset(this.model.value);
        this.hasError.value = false;
        nextTick(() => {
            this.touched.value = false;
        });
    }

    public getData(): T {
        return this.model.value;
    }

    public get dirty() {
        if (this.isFieldDisabled) {
            return false;
        }
        if (!this.model.value && !this.config.default) {
            return false;
        }
        return !this.config.comparator(this.model.value, this.config.default);
    }

    private get isPartiallyValid() {
        const value = this.config.partialValidator(this.model.value);
        if (typeof value === 'boolean') {
            return value;
        }
        return value.isPartial && value.isValid;
    }

    public get invalid() {
        if (this.isFieldDisabled) {
            return false;
        }
        if (this.error) {
            return true;
        }
        if (!toValue(this.config.ignoreTouch) && !this.touched.value) {
            return false;
        }
        if (!toValue(this.config.required) && !this.dirty) {
            return false;
        }
        if (this.isPartiallyValid && this.editing.value) {
            return false;
        }
        return this.isRequiredError || !this.config.validator(this.model.value);
    }

    public clear() {
        if (this.isFieldDisabled) {
            return;
        }
        if (typeof this.model.value === 'string') {
            this.model.value = '' as T;
        } else if (typeof this.model.value === 'number') {
            this.model.value = 0 as T;
        } else if (Array.isArray(this.model.value)) {
            this.model.value = [] as T;
        } else if (typeof this.model.value === 'object') {
            this.model.value = null as T;
        }
    }

    public validate() {
        if (this.isFieldDisabled) {
            return;
        }
        if (!toValue(this.config.required) && !this.dirty) {
            this.hasError.value = false;
            return;
        }
        this.hasError.value =
            this.isRequiredError || !this.config.validator(this.model.value);
    }

    public get error() {
        if (this.isFieldDisabled) {
            return false;
        }
        return this.hasError.value;
    }

    public get disabled() {
        return toValue(this.config.disabled);
    }

    public get hidden() {
        return toValue(this.config.hidden);
    }

    private get isRequiredError() {
        return toValue(this.config.required) && !this.dirty;
    }

    public get errorMessage(): string {
        if (!this.hasError.value) {
            return '';
        }
        const errorMessage = toValue(this.config.errorMessage);
        const requiredError = toValue(this.config.requiredError);
        if (this.isRequiredError) {
            return requiredError;
        } else {
            return errorMessage;
        }
    }

    public touch() {
        this.touched.value = true;
    }

    public get invalidMessage(): string {
        if (!this.invalid) {
            return '';
        }
        const errorMessage = toValue(this.config.errorMessage);
        const requiredError = toValue(this.config.requiredError);
        if (this.isRequiredError) {
            return requiredError;
        } else {
            return errorMessage;
        }
    }

    public get required() {
        return toValue(this.config.required);
    }
}
